import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "../../../components/common/Button";
import Icon from "../../../components/common/Icon";
import Input from "../../../components/common/Input";
import Modal from "../../../components/common/Modal";
import Pagination from "../../../components/common/Pagination";
import JalaliDatePicker from "../../../components/common/JalaliDatePicker";
import Select from "../../../components/common/Select";

import { getClubsApi } from "../../../api/club.api";
import { getPlayersApi } from "../../../api/player.api";
import {
  getTransfersApi,
  createTransferApi,
  deleteTransferApi,
} from "../../../api/transfer.api";

import { TRANSFER_TYPE_LABELS, TRANSFER_TYPE_COLORS } from "../../../utils/constants";
import { toJalali } from "../../../utils/jalali";

function getImageUrl(path) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base =
    process.env.REACT_APP_API_BASE_URL?.replace(/\/api\/v1\/?$/, "") ||
    window.location.origin;
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

const EMPTY_FORM = {
  playerId: "",
  toClubId: "",
  type: "PERMANENT",
  transferDate: new Date(),
  fee: "",
  notes: "",
};

function TransferForm({ onSuccess, onCancel, initialNationalCode }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [playerSearch, setPlayerSearch] = useState("");
  const [playerOptions, setPlayerOptions] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // وقتی از فرم ثبت بازیکن (هشدار کد ملی تکراری) می‌آییم، بازیکن خودکار انتخاب می‌شود
  const autoPickCode = useRef(initialNationalCode || "");

  useEffect(() => {
    if (initialNationalCode) setPlayerSearch(initialNationalCode);
  }, [initialNationalCode]);

  useEffect(() => {
    getClubsApi({ limit: 100 })
      .then(({ data }) => setClubs(data?.data || []))
      .catch(() => setClubs([]));
  }, []);

  useEffect(() => {
    if (!playerSearch || playerSearch.trim().length < 2) {
      setPlayerOptions([]);
      return;
    }
    let active = true;
    const timer = setTimeout(() => {
      getPlayersApi({ search: playerSearch, limit: 8 })
        .then(({ data }) => {
          if (!active) return;
          const list = data?.data || [];
          const exact = autoPickCode.current
            ? list.find((p) => p.nationalCode === autoPickCode.current)
            : null;

          if (exact) {
            autoPickCode.current = "";
            handlePickPlayer(exact);
          } else {
            setPlayerOptions(list);
          }
        })
        .catch(() => {
          if (active) setPlayerOptions([]);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [playerSearch]);

  function handlePickPlayer(player) {
    setSelectedPlayer(player);
    setForm((prev) => ({ ...prev, playerId: player.id }));
    setPlayerSearch(`${player.firstName} ${player.lastName}`);
    setPlayerOptions([]);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.playerId) {
      setError("لطفاً یک بازیکن انتخاب کنید");
      return;
    }
    if (!form.toClubId) {
      setError("لطفاً باشگاه مقصد را انتخاب کنید");
      return;
    }
    if (!form.transferDate) {
      setError("لطفاً تاریخ انتقال را انتخاب کنید");
      return;
    }
    if (
      selectedPlayer?.clubId &&
      Number(selectedPlayer.clubId) === Number(form.toClubId)
    ) {
      setError("باشگاه مقصد نمی‌ تواند همان باشگاه فعلی بازیکن باشد");
      return;
    }

    setSubmitting(true);
    try {
      await createTransferApi({
        playerId: Number(form.playerId),
        toClubId: Number(form.toClubId),
        type: form.type,
        transferDate: form.transferDate.toISOString(),
        fee: form.fee ? Number(form.fee) : undefined,
        notes: form.notes || undefined,
      });
      onSuccess();
    } catch (err) {
      setError(
        err?.response?.data?.message || "ثبت نقل و انتقال با خطا مواجه شد"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const currentClub = clubs.find(
    (c) => c.id === Number(selectedPlayer?.clubId)
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
          {error}
        </div>
      )}

      {/* جستجوی بازیکن */}
      <div className="relative">
        <Input
          label="بازیکن (نام یا کد ملی)"
          required
          placeholder="نام یا کد ملی بازیکن را تایپ کنید..."
          value={playerSearch}
          onChange={(e) => {
            setPlayerSearch(e.target.value);
            setSelectedPlayer(null);
            setForm((prev) => ({ ...prev, playerId: "" }));
          }}
        />

        {playerOptions.length > 0 && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
            {playerOptions.map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => handlePickPlayer(p)}
                className="flex w-full items-center gap-3 px-4 py-3 text-right transition hover:bg-emerald-50"
              >
                <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full bg-slate-100">
                  {p.photo ? (
                    <img
                      src={getImageUrl(p.photo)}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Icon name="userCircle" className="h-5 w-5 text-slate-400" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-700">
                    {p.firstName} {p.lastName}
                  </p>
                  <p className="text-xs text-slate-400">
                    {p.club?.name || "بدون باشگاه"}
                    {p.nationalCode ? ` • ${p.nationalCode}` : ""}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedPlayer && (
        <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-3 text-xs font-bold text-slate-500">
          <Icon name="shield" className="h-4 w-4 text-slate-400" />
          باشگاه فعلی: {currentClub?.name || selectedPlayer?.club?.name || "نامشخص"}
          {selectedPlayer.nationalCode ? ` • کد ملی: ${selectedPlayer.nationalCode}` : ""}
        </div>
      )}

      {/* باشگاه مقصد */}
      <Select
        label="باشگاه مقصد"
        required
        value={form.toClubId}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, toClubId: e.target.value }))
        }
      >
        <option value="">انتخاب کنید...</option>
        {clubs
          .filter((c) => c.id !== Number(selectedPlayer?.clubId))
          .map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
      </Select>

      <div className="grid grid-cols-2 gap-4">
        <Select
          label="نوع انتقال"
          required
          value={form.type}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, type: e.target.value }))
          }
        >
          {Object.entries(TRANSFER_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>

        <JalaliDatePicker
          label="تاریخ انتقال"
          required
          value={form.transferDate}
          onChange={(date) =>
            setForm((prev) => ({ ...prev, transferDate: date }))
          }
          maxDate={new Date()}
        />
      </div>

      <Input
        label="مبلغ انتقال (اختیاری، تومان)"
        type="number"
        min="0"
        placeholder="مثلاً 500000000"
        value={form.fee}
        onChange={(e) => setForm((prev) => ({ ...prev, fee: e.target.value }))}
      />

      <div>
        <label className="mb-2 block text-sm font-bold text-slate-600">
          توضیحات (اختیاری)
        </label>
        <textarea
          rows={3}
          value={form.notes}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, notes: e.target.value }))
          }
          className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:border-emerald-400"
          placeholder="توضیح اضافی درباره این نقل و انتقال..."
        />
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          انصراف
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "در حال ثبت..." : "ثبت نقل و انتقال"}
        </Button>
      </div>
    </form>
  );
}

export default function TransfersManagement() {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [prefillCode, setPrefillCode] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ message: "", type: "success" });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    totalPages: 1,
  });

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ message, type });
    setTimeout(() => setSnackbar({ message: "", type: "success" }), 3500);
  };

  const loadTransfers = useCallback(() => {
    setLoading(true);
    getTransfersApi({
      search: search || undefined,
      type: type || undefined,
      page: pagination.page,
      limit: pagination.limit,
    })
      .then(({ data }) => {
        setTransfers(data?.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data?.meta?.total ?? 0,
          totalPages: data?.meta?.totalPages ?? 1,
        }));
      })
      .catch(() => {
        setTransfers([]);
      })
      .finally(() => setLoading(false));
  }, [search, type, pagination.page, pagination.limit]);

  useEffect(() => {
    loadTransfers();
  }, [loadTransfers]);

  // ورود از فرم ثبت بازیکن (هشدار کد ملی تکراری): فرم انتقال با همان بازیکن باز می‌شود
  useEffect(() => {
    const code = location.state?.nationalCode;
    if (code) {
      setPrefillCode(code);
      setFormOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const closeForm = () => {
    setFormOpen(false);
    setPrefillCode("");
  };

  const handleCreated = () => {
    setPrefillCode("");
    setFormOpen(false);
    showSnackbar("نقل و انتقال با موفقیت ثبت شد");
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadTransfers();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTransferApi(deleteTarget.id);
      showSnackbar("نقل و انتقال حذف شد");
      setDeleteTarget(null);
      loadTransfers();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || "حذف با خطا مواجه شد",
        "error"
      );
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-text-dark">
            نقل و انتقالات بازیکنان
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            ثبت و پیگیری انتقال بازیکنان بین باشگاه‌ها
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)}>
          <Icon name="plus" className="h-4 w-4" />
          ثبت نقل و انتقال جدید
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="relative flex-1 min-w-[220px]">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="جستجوی نام بازیکن..."
            className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pr-10 pl-4 text-sm outline-none focus:border-emerald-400"
          />
          <Icon
            name="search"
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          />
        </div>

        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-4 text-xs font-bold text-slate-600 outline-none focus:border-emerald-400"
        >
          <option value="">همه انواع</option>
          {Object.entries(TRANSFER_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-right text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-xs font-black text-slate-500">
                  بازیکن
                </th>
                <th className="px-4 py-3 text-xs font-black text-slate-500">
                  از باشگاه
                </th>
                <th className="px-4 py-3 text-xs font-black text-slate-500" />
                <th className="px-4 py-3 text-xs font-black text-slate-500">
                  به باشگاه
                </th>
                <th className="px-4 py-3 text-center text-xs font-black text-slate-500">
                  نوع
                </th>
                <th className="px-4 py-3 text-center text-xs font-black text-slate-500">
                  تاریخ
                </th>
                <th className="px-4 py-3 text-center text-xs font-black text-slate-500">
                  مبلغ
                </th>
                <th className="px-4 py-3 text-center text-xs font-black text-slate-500">
                  عملیات
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="8" className="py-10 text-center text-slate-400">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : transfers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="py-10 text-center text-slate-400">
                    هنوز نقل و انتقالی ثبت نشده است
                  </td>
                </tr>
              ) : (
                transfers.map((t) => (
                  <tr key={t.id} className="transition hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full bg-slate-100">
                          {t.player?.photo ? (
                            <img
                              src={getImageUrl(t.player.photo)}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <Icon
                              name="userCircle"
                              className="h-4 w-4 text-slate-400"
                            />
                          )}
                        </div>
                        <span className="font-bold text-slate-700">
                          {t.player
                            ? `${t.player.firstName} ${t.player.lastName}`
                            : "نامشخص"}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-slate-500">
                      {t.fromClub?.name || "—"}
                    </td>

                    <td className="px-1 py-3 text-slate-300">
                      <Icon name="arrowLeft" className="h-4 w-4" />
                    </td>

                    <td className="px-4 py-3 font-bold text-slate-700">
                      {t.toClub?.name || "—"}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-black ${
                          TRANSFER_TYPE_COLORS[t.type] ||
                          "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {TRANSFER_TYPE_LABELS[t.type] || t.type}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-center text-xs text-slate-500">
                      {t.transferDate ? toJalali(t.transferDate) : "—"}
                    </td>

                    <td className="px-4 py-3 text-center text-xs text-slate-500">
                      {t.fee ? Number(t.fee).toLocaleString("fa-IR") : "—"}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setDeleteTarget(t)}
                        className="rounded-lg p-2 text-red-400 transition hover:bg-red-50 hover:text-red-600"
                        title="حذف"
                      >
                        <Icon name="trash" className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={(page) =>
            setPagination((prev) => ({ ...prev, page }))
          }
        />
      )}

      {/* Create modal */}
      <Modal
        open={formOpen}
        onClose={closeForm}
        title="ثبت نقل و انتقال جدید"
        size="lg"
      >
        <TransferForm
          onSuccess={handleCreated}
          onCancel={closeForm}
          initialNationalCode={prefillCode}
        />
      </Modal>

      {/* Delete confirm modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="حذف نقل و انتقال"
        size="sm"
      >
        <p className="text-sm text-slate-600">
          آیا از حذف انتقال{" "}
          <strong>
            {deleteTarget?.player?.firstName} {deleteTarget?.player?.lastName}
          </strong>{" "}
          به «{deleteTarget?.toClub?.name}» مطمئن هستید؟ این عملیات باشگاه فعلی
          بازیکن را به باشگاه قبلی برمی‌ گرداند.
        </p>
        <p className="mt-2 text-xs text-slate-400">
          توجه: فقط آخرین نقل‌ وانتقال ثبت‌ شده‌ی هر بازیکن قابل حذف است.
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
            انصراف
          </Button>
          <Button
            variant="primary"
            className="!bg-red-500 shadow-red-500/25 hover:!bg-red-600"
            onClick={handleDelete}
          >
            حذف
          </Button>
        </div>
      </Modal>

      {/* Snackbar */}
      {snackbar.message && (
        <div
          className={`fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl px-5 py-3 text-sm font-bold text-white shadow-xl ${
            snackbar.type === "error" ? "bg-red-500" : "bg-emerald-500"
          }`}
        >
          {snackbar.message}
        </div>
      )}
    </div>
  );
}